import React from "react";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { CopyAllOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";

import { useEntityDetailContext } from "contexts/EntityDetailContext";

import { BaseTabPanel } from "components/Shared/Base/BaseTabPanel";
import AppTable from "components/Shared/AppTable";
import InformationTile from "components/Shared/InformationTile";

import { useGenerateApiCredentialMutation } from "store/services/entityApi";
import { useLazyListMetricsQuery } from "store/services/metricApi";

import { useTablePagination } from "hooks";

import { localizeDateTimeShort } from "utils/dateFormats";
import { BASE_CONSTANTS } from "constants/baseConstants";

export const EntityAPISetupTab = () => {
  const [mode, setMode] = React.useState("overview");

  return (
    <>
      <BaseTabPanel.Header label="API Setup">
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={mode}
          onChange={(_, mode) => {
            if (mode !== null) setMode(mode);
          }}
        >
          <ToggleButton value="overview">Overview</ToggleButton>

          <ToggleButton value="metrics">Metrics</ToggleButton>
        </ToggleButtonGroup>
      </BaseTabPanel.Header>

      <Collapse in={mode === "overview"}>
        <APIOverview />
      </Collapse>

      <Collapse in={mode === "metrics"}>
        <APIMetrics />
      </Collapse>
    </>
  );
};

const APIOverview = () => {
  const { entityDetail } = useEntityDetailContext();

  const apiCredential = entityDetail?.preference?.apiCredential;

  const apiKeyUsageInPercentage = Math.round(
    (apiCredential?.currentUsage / apiCredential?.quotaLimit) * 100
  );

  // RTK Query
  const [generateApiCredential, { isLoading }] =
    useGenerateApiCredentialMutation();

  // methods
  const handleGenerateCredential = async () => {
    try {
      await generateApiCredential(entityDetail?._id).unwrap();
      toast.success("API Credential successfully generated!");
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't generate credentials!");
    }
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        gap={BASE_CONSTANTS.SPACING_SM}
        padding={BASE_CONSTANTS.PADDING_MD}
      >
        <Box
          display="flex"
          flexDirection="column"
          flexGrow={1}
          gap={BASE_CONSTANTS.SPACING_SM}
        >
          <InformationTile title="API Key">
            {apiCredential?.key ? (
              <div className="flex items-center gap-2">
                <div className="tracking-widest">{apiCredential?.key}</div>

                <IconButton
                  size="small"
                  color="primary"
                  onClick={() =>
                    navigator.clipboard
                      .writeText(apiCredential?.key)
                      .then(() => toast.info("API key copied!"))
                  }
                >
                  <CopyAllOutlined fontSize="small" />
                </IconButton>
              </div>
            ) : (
              "N/A"
            )}
          </InformationTile>

          <InformationTile title="Limit" subtitle={apiCredential?.quotaLimit} />

          <InformationTile
            title="Current Usage"
            subtitle={apiCredential?.currentUsage}
          />
        </Box>

        <div className="flex flex-col gap-2">
          {!!apiCredential && (
            <div className="relative inline-flex">
              {(() => {
                const thickness = 6;
                const size = 150;

                const getProgressIndicator = () => {
                  if (apiKeyUsageInPercentage <= 30) return "success";

                  if (
                    apiKeyUsageInPercentage > 30 &&
                    apiKeyUsageInPercentage <= 80
                  )
                    return "warning";

                  return "error";
                };

                return (
                  <CircularProgress
                    size={size}
                    value={apiKeyUsageInPercentage}
                    variant="determinate"
                    color={getProgressIndicator()}
                    thickness={thickness}
                    sx={{
                      borderRadius: "50%",
                      boxShadow: `inset 0 0 0 ${
                        (thickness / 44) * size
                      }px lightgray`,
                    }}
                  />
                );
              })()}

              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-sm">
                <div className="text-center text-xl">
                  {apiKeyUsageInPercentage} %
                </div>

                <div className="text-center text-[12px] text-gray-500">
                  Exhausted
                </div>
              </div>
            </div>
          )}

          <LoadingButton loading={isLoading} onClick={handleGenerateCredential}>
            {!!apiCredential ? "Reset API Quota" : "Generate API Key"}
          </LoadingButton>
        </div>
      </Box>

      <a
        className="px-4 text-blue-600 underline"
        href={`http://localhost:5090/api/accounts/public?apiKey=${apiCredential?.key}`}
        target="_blank"
        rel="noreferrer"
      >
        Click here to test metrics (For Development Only)
      </a>
    </>
  );
};

const APIMetrics = () => {
  const { entityDetail } = useEntityDetailContext();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const STATUS_INDICATOR_MAP = {
    2: "bg-green-500 text-green-50",
    3: "bg-blue-500 text-blue-50",
    4: "bg-orange-500 text-orange-50",
    5: "bg-red-500 text-red-100",
  };

  const columns = [
    {
      name: "Endpoint",
      selector: (row) => row?.endpoint,
      minWidth: "400px",
    },
    {
      name: "Method",
      selector: (row) => row?.method,
    },
    {
      name: "Status Code",
      cell: (row) => {
        const indicatorIndex = row?.statusCode?.toString()?.charAt(0);
        const indicatorClasses = STATUS_INDICATOR_MAP[indicatorIndex] || "";

        return (
          <div
            className={twMerge(
              "rounded px-1.5 py-0.5 text-sm",
              indicatorClasses
            )}
          >
            {row?.statusCode}
          </div>
        );
      },
    },
    {
      name: "Response Time",
      cell: (row) => Number(row?.responseTime)?.toPrecision(4) + " ms",
    },
    {
      name: "Request Date",
      cell: (row) => localizeDateTimeShort(row?.createdAt),
    },
    {
      name: "Key Used",
      cell: (row) => row?.apiKey,
    },
  ];

  // RTK Query
  const [fetchMetrics, { data: metricRes, isLoading, isFetching }] =
    useLazyListMetricsQuery();

  React.useEffect(() => {
    fetchMetrics({
      page: currentPage,
      limit: perPage,
      entity: entityDetail?._id,
    });
  }, [fetchMetrics, entityDetail, currentPage, perPage]);

  return (
    <AppTable
      data={metricRes?.data ?? []}
      columns={columns}
      progressPending={isLoading || isFetching}
      paginationServer
      paginationPerPage={perPage}
      paginationTotalRows={metricRes?.totalItems ?? 0}
      paginationDefaultPage={currentPage}
      onChangePage={handlePageChange}
      onChangeRowsPerPage={handlePerRowsChange}
    />
  );
};
